import { Component, ErrorInfo, lazy, ReactNode, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'
import { AdminShell, type AdminShellWidth } from './components/AdminShell'
import { CustomerWorkspacePage } from './pages/CustomerWorkspacePage'
import { EntryPage } from './pages/EntryPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { OrderFormPrintPage } from './pages/OrderFormPrintPage'
import { loadAdminCatalogPage, loadAdminContractsPage, loadAdminDocumentsPage, loadAdminOrderFormPage, loadAdminWorkspaceListPage, loadAdminWorkspacePage, loadPricingLabPage, loadUiReviewPage, loadPartnerAreaPage, loadAdminPartnersPage, loadAdminPartnerPage, loadAdminPartnerKitPage, loadAdminPartnerRegistrationsPage } from './app-route-modules'

const AdminWorkspacePage = lazy(loadAdminWorkspacePage)
const AdminWorkspaceListPage = lazy(loadAdminWorkspaceListPage)
const AdminCatalogPage = lazy(loadAdminCatalogPage)
const AdminDocumentsPage = lazy(loadAdminDocumentsPage)
const AdminContractsPage = lazy(loadAdminContractsPage)
const AdminOrderFormPage = lazy(loadAdminOrderFormPage)
const PartnerAreaPage = lazy(loadPartnerAreaPage)
const AdminPartnersPage = lazy(loadAdminPartnersPage)
const AdminPartnerPage = lazy(loadAdminPartnerPage)
const AdminPartnerKitPage = lazy(loadAdminPartnerKitPage)
const AdminPartnerRegistrationsPage = lazy(loadAdminPartnerRegistrationsPage)

// Dev-only: /pricing-lab is registered below only when import.meta.env.DEV is true, so
// Rollup dead-code-eliminates this dynamic import (and the whole route) from production
// builds. See PricingLabPage.tsx for what it renders.
const PricingLabPage = import.meta.env.DEV ? lazy(loadPricingLabPage) : null
// Dev-only: /ui-review renders every shared UI primitive in every state. Same pattern as /pricing-lab.
const UiReviewPage = import.meta.env.DEV ? lazy(loadUiReviewPage) : null

/**
 * Every admin page gets the persistent admin navigation (AdminShell). The shell sits
 * outside Suspense, so the navigation stays in view while a page module loads.
 * shell="full" lets the page own its width (the workspace editor). shell={false}
 * keeps a page full screen with no navigation (the order form editor).
 */
function AdminRoute({ children, shell = 'standard' }: { children: ReactNode; shell?: AdminShellWidth | false }) {
  const page = (
    <AdminRouteErrorBoundary>
      <Suspense fallback={<RouteLoading />}>{children}</Suspense>
    </AdminRouteErrorBoundary>
  )
  return shell ? <AdminShell width={shell}>{page}</AdminShell> : page
}

function RouteLoading() {
  return <main className="state-page"><div className="loading-lockup" role="status">Opening workspace tools…</div></main>
}

class AdminRouteErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Admin route failed to load', error, info)
  }

  render() {
    if (this.state.hasError) {
      return <main className="state-page"><div className="state-panel"><h1>Workspace tools unavailable</h1><p>Refresh the page to try loading the admin workspace again.</p><button className="button button-primary" type="button" onClick={() => window.location.reload()}>Refresh</button></div></main>
    }
    return this.props.children
  }
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<EntryPage />} />
      <Route path="/admin/workspaces" element={<AdminRoute><AdminWorkspaceListPage /></AdminRoute>} />
      <Route path="/admin/catalog" element={<AdminRoute><AdminCatalogPage /></AdminRoute>} />
      <Route path="/admin/documents" element={<AdminRoute><AdminDocumentsPage /></AdminRoute>} />
      <Route path="/admin/contracts" element={<AdminRoute><AdminContractsPage /></AdminRoute>} />
      <Route path="/admin/partners" element={<AdminRoute><AdminPartnersPage /></AdminRoute>} />
      <Route path="/admin/partners/kit" element={<AdminRoute><AdminPartnerKitPage /></AdminRoute>} />
      <Route path="/admin/partners/registrations" element={<AdminRoute><AdminPartnerRegistrationsPage /></AdminRoute>} />
      <Route path="/admin/partners/:partnerId" element={<AdminRoute><AdminPartnerPage /></AdminRoute>} />
      <Route path="/admin/workspaces/:workspaceId" element={<AdminRoute shell="full"><AdminWorkspacePage /></AdminRoute>} />
      {import.meta.env.DEV && PricingLabPage && (
        <Route path="/pricing-lab" element={<Suspense fallback={<RouteLoading />}><PricingLabPage /></Suspense>} />
      )}
      {import.meta.env.DEV && UiReviewPage && (
        <Route path="/ui-review" element={<Suspense fallback={<RouteLoading />}><UiReviewPage /></Suspense>} />
      )}
      <Route path="/admin/workspaces/:workspaceId/order-forms/:formId" element={<OrderFormPrintPage admin />} />
      <Route path="/admin/workspaces/:workspaceId/order-forms/:formId/edit" element={<AdminRoute shell={false}><AdminOrderFormPage /></AdminRoute>} />
      <Route path="/partner/:slug/*" element={<Suspense fallback={<main className="state-page"><div className="loading-lockup" role="status">Opening your partner area…</div></main>}><PartnerAreaPage /></Suspense>} />
      <Route path="/:publicId" element={<CustomerWorkspacePage />} />
      <Route path="/:publicId/order-forms/:formId" element={<OrderFormPrintPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
